// Who sent the merchant to Stripe-hosted onboarding.
//  - "redeem"   → the balance CTA (features/balance-cta), mid-redeem
//  - "settings" → Settings ▸ Payment account (features/payment-account)
export type OnboardingSource = "redeem" | "settings";

const SOURCE_KEY = "gp.stripeOnboardingSource";

// Both entry points hand off to the SAME Stripe account link, in this same tab,
// and Stripe's return_url is identical for both — so the tab has to remember
// which one started the trip or the return can't be routed back to it.
// sessionStorage survives the cross-origin round trip (same tab, same origin on
// the way back) and dies with the tab, which is exactly the lifetime we want.
export function markOnboardingSource(source: OnboardingSource) {
  try {
    window.sessionStorage.setItem(SOURCE_KEY, source);
  } catch {
    // Storage blocked (private mode / cookie policy). Not fatal: the return
    // still lands, it just falls back to the redeem handler below.
  }
}

// Read-only on purpose — the marker is NOT cleared once it's been used. One
// onboarding trip can bounce through several document loads, and clearing on
// the first would misroute the rest:
//  - Back out of Stripe to the dashboard, then Forward to finish: the middle
//    load carries no stripe_* params, and a clear there would strand the return.
//  - Back AFTER finishing: account links are single-use, so Stripe answers the
//    revisited link with refresh_url — another return, on a marker we'd have
//    already spent. It would fall through to "redeem" and pop a Redeem modal at
//    someone who only ever opened Settings.
// Leaving it costs nothing: markOnboardingSource overwrites it at the start of
// the next trip, it can only be read on a URL Stripe redirected to, and
// sessionStorage dies with the tab.
function readSource(): OnboardingSource | null {
  try {
    const value = window.sessionStorage.getItem(SOURCE_KEY);
    return value === "redeem" || value === "settings" ? value : null;
  } catch {
    return null;
  }
}

let consumed = false;

// True at most once per document, and only for the flow that started the
// onboarding — the other hook gets false and stays out of the way. A missing
// marker (storage blocked, or the link reopened in a different tab than the one
// that started it) falls back to "redeem": that flow owned every return before
// settings got its own handler.
//
// The LIVE url is read here rather than a snapshot taken at module load, and
// only the winner strips it. Two reasons, in this order:
//  - A snapshot outlives a sign-out. Identity changes in this app without a
//    document load (ProtectedRoute answers a dead session with
//    <Navigate to="/">, login SPA-navigates back to /merchant), so a return
//    that nobody was authenticated to handle would lie in wait in module state
//    and fire at whoever signs in next on a shared terminal. The query string
//    is the one piece of state that redirect reliably takes with it.
//  - Stripping only on a win keeps the two hooks order-independent: the loser
//    can read before OR after the winner and reaches the same verdict.
export function consumeOnboardingReturn(source: OnboardingSource): boolean {
  if (consumed) return false;

  const params = new URLSearchParams(window.location.search);
  const returnedFromStripe =
    params.get("stripe_return") === "true" ||
    params.get("stripe_refresh") === "true";
  if (!returnedFromStripe) return false;

  const marker = readSource();
  const isMine =
    marker === source || (marker === null && source === "redeem");
  if (!isMine) return false;

  consumed = true;
  // Drop the params now that this flow owns the return, so a reload doesn't
  // replay the check.
  window.history.replaceState({}, "", window.location.pathname);
  return true;
}
